import { NgStyle } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import {
	FormsModule,
	ReactiveFormsModule,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import { NotificationType } from "src/app/enums";
import { ApiService } from "../../../../services/api.service";
import { NotificationService } from "../../../../services/notification/notification.service";
import { UserStorageService } from "../../../../services/user-storage.service";

@Component({
	selector: "app-notifications-message",
	templateUrl: "./notifications-message.component.html",
	styleUrls: ["./notifications-message.component.scss"],
	imports: [
		FormsModule,
		ReactiveFormsModule,
		NgxIntlTelInputModule,
		NgStyle,
		TranslatePipe,
	],
})
export class NotificationsMessageComponent implements OnInit {
	private userStorageService = inject(UserStorageService);
	private apiService = inject(ApiService);
	private activeRoute = inject(ActivatedRoute);
	private notificationService = inject(NotificationService);
	private router = inject(Router);

	lang = this.userStorageService.getLanguage();
	public translate: TranslateService;
	public isLoading: boolean = true;
	messageId: any;
	message: any;
	selectedFiles: any;
	errorMessage: string = "";
	successMessage: string = "";
	isReplyMessage: boolean = false;
	isSendingMessage: boolean = false;
	messageFormSubmitted: boolean = false;
	messageForm!: UntypedFormGroup;
	progress: number = 0;

	constructor() {
		const translate = inject(TranslateService);

		this.translate = translate;
	}

	ngOnInit(): void {
		this.apiService.CheckLogin();
		this.translate.use(this.lang.shortcode);

		this.activeRoute.paramMap.subscribe((params) => {
			this.messageId = params.get("id") || "";
		});

		this.getMessage();
	}

	getMessage = () => {
		this.notificationService.getMessage(this.messageId).then((message: any) => {
			this.message = message;

			if (message.IsNew) {
				this.setMessageToRead();
			}

			this.isLoading = false;
		});
	};

	setMessageToRead = () => {
		this.notificationService.setMessageToRead(this.messageId);
	};

	downloadFile = (file: any) => {
		window.location.href = this.apiService.GetReportDownloadUrl(
			file.DownloadUrl,
		);
	};

	back = () => window.history.back();

	isWhistleblower = () => this.message.Type === NotificationType.whistleblower;

	gotoIncidentDetail = () =>
		this.router.navigate([
			`whistleblower/case/detail/${this.message.ReferenceId}`,
		]);

	initReplyMessage = () => {
		this.successMessage = "";
		this.errorMessage = "";
		this.isReplyMessage = true;
		this.messageFormSubmitted = false;
		this.initMessageForm();
	};

	initMessageForm = () => {
		this.selectedFiles = [];
		this.messageForm = new UntypedFormGroup({
			ReferenceId: new UntypedFormControl(this.message.ReferenceId),
			RecipientId: new UntypedFormControl(this.message.SenderId),
			Subject: new UntypedFormControl("", [Validators.required]),
			MessageBody: new UntypedFormControl("", [Validators.required]),
			Files: new UntypedFormControl(null),
		});
	};

	replyMessage() {
		this.errorMessage = "";
		this.messageFormSubmitted = true;
		this.progress = 0; // Start progress at 0

		if (this.messageForm.valid) {
			this.isSendingMessage = true;
			this.progress = this.getRandomNumber(0, 50);

			this.apiService
				.SendReply(this.messageForm.value)
				.then((_: any) => {
					this.progress = 100;
					this.successMessage = "message_sent";
					setTimeout(() => {
						this.isSendingMessage = false;
						this.isReplyMessage = false;
					}, 5000);
				})
				.catch((error: any) => {
					this.progress = 0;
					console.error(error);
					this.errorMessage = "error_has_occured";
					this.isSendingMessage = false;
				});
		}
	}

	getRandomNumber(min: number, max: number): number {
		return Math.random() * (max - min) + min;
	}

	// Uploading file

	onImagePicked(event: any) {
		this.errorMessage = "";
		const files: FileList = event.target.files;
		const totalSizeLimit = 300 * 1024 * 1024; // 300MB in bytes
		let totalSize = 0;

		if (files && files.length > 0) {
			// Iterate through each selected file
			for (const file of files) {
				totalSize += file.size;
			}

			// Initialize an array to store promises for each file conversion
			this.selectedFiles = [];

			// Check if total size exceeds limit
			if (totalSize > totalSizeLimit) {
				this.errorMessage = "error_upload_file_size_exceed";
				// Handle the case where the total size exceeds the limit, for example, show an error message
				return;
			}

			// Initialize an array to store promises for each file conversion
			this.selectedFiles = [];
			// Iterate through each selected file
			for (const file of files) {
				this.convertFileToBase64(file).then((fileData: string) => {
					const incidentFile = { name: file.name, base64: fileData };
					this.selectedFiles.push(incidentFile);
					//this.ref.detectChanges();
				});
			}

			this.messageForm.get("Files")?.setValue(this.selectedFiles);
			this.messageForm.get("Files")?.updateValueAndValidity();

			//this.ref.detectChanges();
		}
	}

	convertFileToBase64(file: File): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const base64String = reader.result as string;
				resolve(base64String.split(",")[1]); // Extract base64 data (omit the data URL prefix)
			};
			reader.onerror = () => {
				reject(new Error("Error reading file"));
			};
			reader.readAsDataURL(file);
		});
	}

	removeFile(index: number) {
		this.selectedFiles.splice(index, 1); // Remove the date at the given index
	}
}
